/**
 * @lines98/client/services/daily-status-prefetch — РАННИЙ прогрев СТАТУСА доски дня
 * (idle / done) на boot, чтобы ПЕРВЫЙ заход на доску дня был ТЁПЛЫМ (без нейтрального
 * loading-кадра / idle→done мелькания), instant-ui-hydration §4.
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-decision 2026-06-11, fix/play-as-start-page):
 * Лендинг теперь — экран-пикер «Играть» (modeSelect), а НЕ доска дня (см. `app.ts`).
 * Это даёт буфер времени: пока игрок на пикере, фоном прогреваем статус доски дня,
 * чтобы первый заход (nav-rail «Доска дня») рисовался СИНХРОННО из снимка.
 *
 * Что уже грело раньше при прямом лендинге на доску дня:
 *   - `prefetchDailySeed` (daily-api) — in-memory seed-кэш (`getCachedDailySeed`).
 *   - `prefetchLeaderboard` (leaderboard-prefetch) — стартовая страница борда «Сегодня».
 * Чего НЕ хватало: played-снимок / idle-маркер ставит ТОЛЬКО `daily-board-screen.boot()`
 * — он async-резолвит «сыграно сегодня?» (IDB `loadPlayed`) и при «не сыграно» пишет
 * date-stamped idle-маркер. До первого mount доски дня этих снимков НЕТ → раньше первый
 * заход шёл через нейтральный loading-кадр (instant-ui-hydration §1 case 3). Этот модуль
 * выполняет ТОТ ЖЕ статус-detect ЗАРАНЕЕ (на boot, после готовности identity), не монтируя
 * экран → к первому заходу снимок уже лёг, доска рисуется мгновенно (idle ИЛИ done).
 *
 * **НЕ дублирует логику — переиспользует существующие модули** (code-structure §1):
 *   - `getDailySeed` / `getCachedDailySeed` (daily-api) — тот же легальный публичный GET.
 *   - `loadPlayed` (daily-cache) — IDB-канон played; сам БЭКФИЛЛИТ синхронный played-снимок.
 *   - `loadInProgress` (daily-cache) — recovery-маркер (есть незавершённая партия?).
 *   - `saveIdleMarker` (daily-played-snapshot) — date-stamped «сегодня не сыграно».
 * Тот же порядок проверок, что в `daily-board-screen.boot()` (played → in-progress → idle),
 * чтобы прогрев и mount-reconcile были идемпотентны (mount-boot — no-op при совпадении).
 *
 * **Per-player + identity-gated:** played-снимок / idle-маркер keyed по `playerId`
 * (изоляция, instant-ui-hydration §3) — их write — no-op без identity. Поэтому ЗАПУСКАТЬ
 * ПОСЛЕ `bootIdentity` (как `prefetchMetaState`/`prefetchLeaderboard`): иначе снимок-write
 * проглатывается и первый заход остаётся холодным. Сам seed публичный (без Bearer) — его
 * греет `prefetchDailySeed` вне identity-гейта (этот модуль лишь переиспользует тёплый кэш,
 * а на промахе делает свой GET).
 *
 * **Server-truth / анти-чит цел:** это просто более ранний/недублированный набор легальных
 * операций (публичный `GET /daily/seed` + локальные IDB-read'ы). Ничего не верифицируется и
 * не пишется в зачёт; «одна попытка/день» — серверный `daily_attempts`; снимки — UX-проекция,
 * движок/replayScore их не импортируют. mount-boot всегда reconcile'ит к server-truth (IDB
 * `loadPlayed`) идемпотентно.
 *
 * **In-progress НЕ трогаем намеренно.** Если есть незавершённая партия (refresh-recovery),
 * idle-маркер НЕ ставим — recovery deep-link (resume) принадлежит экрану (`boot()` ведёт
 * на board). Прогрев только готовит idle/done-кадр; resume — на экране, без изменений.
 *
 * Best-effort: любая ошибка/offline проглатывается и НИКОГДА не ломает boot — снимок просто
 * остаётся холодным, доска дня идёт обычным путём (нейтральный кадр → статус-detect в boot).
 *
 * Cross-references:
 *   - services/daily-api.ts `prefetchDailySeed` / `getCachedDailySeed` (прецедент + seed-кэш).
 *   - services/daily-cache.ts `loadPlayed` / `loadInProgress` (IDB-канон + бэкфилл снимка).
 *   - lib/daily-played-snapshot.ts `saveIdleMarker` (date-stamped idle-маркер).
 *   - screens/daily-board-screen.ts `boot()` (тот же статус-detect — mount reconcile).
 *   - .claude/skills/instant-ui-hydration/SKILL.md §4 (boot-prefetch к первому заходу) + §1.
 */

import { getCachedDailySeed, getDailySeed } from './daily-api.js';
import { loadInProgress, loadPlayed, savePlayed } from './daily-cache.js';
import { saveIdleMarker } from '../lib/daily-played-snapshot.js';
import { gateOwnDailyStats } from '../lib/daily-server-gate.js';

/**
 * Fire-and-forget прогрев статуса доски дня к первому заходу (как `prefetchDailySeed`):
 *   1. Берём `date_of_day` из ТЁПЛОГО seed-кэша (`prefetchDailySeed` обычно уже его
 *      залил), иначе делаем свой `getDailySeed` (тот же публичный GET; он же заполнит
 *      seed-кэш). На ошибке/offline тихо выходим (снимок остаётся холодным).
 *   2. `loadPlayed(dateOfDay)` — IDB-канон played. Сыграно → он САМ бэкфиллит синхронный
 *      played-снимок (см. `loadPlayed`) → первый mount нарисует DONE мгновенно. Выходим.
 *   2b. Локально НЕ сыграно → server-truth (`gateOwnDailyStats`): играл ли ЭТОТ аккаунт
 *      доску на ДРУГОМ браузере (cross-browser double-play fix)? Да → backfill played-
 *      снимка (`savePlayed`) → переход пикер→доска корректен (done БЕЗ idle→done флипа)
 *      даже на 2-м браузере. Выходим. (Дедупится с гейтом на экране — тот же `gateOwnDailyStats`.)
 *   3. Не сыграно (и сервер не знает аккаунт) → `loadInProgress` — есть незавершённая
 *      партия? Да → idle-маркер НЕ ставим (recovery resume принадлежит экрану). Выходим.
 *   4. Не сыграно И нет in-progress → `saveIdleMarker(dateOfDay)` → первый mount нарисует
 *      IDLE мгновенно (стартовая доска-превью из тёплого seed), без нейтрального кадра.
 *
 * Тот же порядок проверок, что `daily-board-screen.boot()` → mount-boot будет
 * идемпотентным reconcile'ом (no-op при совпадении). НЕ блокирует рендер: без await.
 */
export function prefetchDailyStatus(): void {
  void warmDailyStatus().catch(() => {
    /* best-effort прогрев — offline / ошибка ок, снимок остаётся холодным */
  });
}

/** Внутренняя async-работа прогрева (см. шаги в `prefetchDailyStatus`). */
async function warmDailyStatus(): Promise<void> {
  // 1) day-of-day: тёплый seed-кэш (само-инвалидируется на rollover), иначе свой GET.
  const cached = getCachedDailySeed();
  let dateOfDay: string;
  if (cached) {
    dateOfDay = cached.dateOfDay;
  } else {
    const seedResult = await getDailySeed();
    if (!seedResult.ok) return; // network / rate-limit — холодный путь, mount-boot решит
    dateOfDay = seedResult.dateOfDay;
  }

  // 2) played-канон (IDB) — сам бэкфиллит синхронный played-снимок при «сыграно».
  const played = await loadPlayed(dateOfDay);
  if (played) return; // done-снимок прогрет → первый mount нарисует DONE мгновенно

  // 2b) Локально не сыграно, но ЭТОТ АККАУНТ мог играть на ДРУГОМ браузере (cross-browser
  //     double-play fix). server-truth (`gateOwnDailyStats` — дедупится с гейтом на экране):
  //     сервер вернул запись → backfill played-снимка, чтобы переход пикер→доска был done
  //     БЕЗ idle→done флипа даже на 2-м браузере. `finalBoard` нет (чужое устройство) —
  //     done-доска восстановится по shortId в `hydrateDone`. На null (не играл / гость /
  //     сетевой сбой) — идём дальше к idle (fail-open: сервер backstop-ит на сабмите).
  const serverStats = await gateOwnDailyStats(dateOfDay);
  if (serverStats) {
    await savePlayed({
      dateOfDay,
      scoreVerified: serverStats.scoreVerified,
      replayId: null,
      linesVerified: serverStats.linesVerified,
      shortId: serverStats.shortId,
      lastRank: serverStats.rank,
      timestamp: Date.now(),
    });
    return; // played-снимок прогрет → первый mount нарисует DONE мгновенно
  }

  // 3) in-progress (refresh-recovery) — idle-маркер НЕ ставим, resume принадлежит экрану.
  const inProgress = await loadInProgress(dateOfDay);
  if (inProgress) return;

  // 4) подтверждённо «не сыграно» и без незавершённой партии → date-stamped idle-маркер.
  //    Следующий (первый) mount доски дня прочитает его СИНХРОННО → idle сразу.
  saveIdleMarker(dateOfDay);
}
